import { useQuery } from 'react-query';
import { Edge, MarkerType, Node } from 'reactflow';
import { Dispatch, SetStateAction } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useApiContext } from 'context/ApiContext';
import { KEYS } from 'helpers/react-query.config';
import { TicketFlowGraph } from 'api/client';
import { Status } from 'helpers/app.config';
import { getRandomColor } from 'helpers/utils';

interface Props {
  id: number;
  setNodes: Dispatch<SetStateAction<Node[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
}

// Paso 1: Calcular niveles de cada nodo desde el inicial
const calculateLevels = (
  initialId: number,
  allStatuses: TicketFlowGraph[]
): Record<string, number> => {
  const levels: Record<string, number> = {};
  const visited = new Set<string>();
  const queue: { id: string; level: number }[] = [
    { id: `${initialId}`, level: 0 }
  ];

  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    if (visited.has(id)) continue;

    visited.add(id);
    levels[id] = level;

    const status = allStatuses.find((s) => s.ticketStatusId === +id);
    status?.relatedStatus?.forEach((rel) => {
      queue.push({ id: `${rel.ticketStatusId}`, level: level + 1 });
    });
  }

  return levels;
};

// Paso 2: Crear nodos posicionados para layout
const createNodes = (
  allStatuses: TicketFlowGraph[],
  levels: Record<string, number>,
  initialId: number
): Node[] => {
  const spacingX = 250;
  const spacingY = 80;

  // Contador por nivel para colocar nodos uno debajo del otro
  const verticalIndexPerLevel: Record<number, number> = {};

  return (allStatuses || [])?.map((status) => {
    const id = `${status.ticketStatusId}`;
    const level = levels[id] ?? 0;

    // Calcular en qué fila (y) debe ir este nodo en su nivel
    if (!verticalIndexPerLevel[level]) {
      verticalIndexPerLevel[level] = 0;
    }
    const indexInLevel = verticalIndexPerLevel[level]++;

    return {
      id,
      data: {
        label: status?.name,
        color: Status[status?.ticketStatusId]?.color || getRandomColor(),
        isInitial: status.ticketStatusId === initialId
      },
      position: {
        x: level * spacingX,
        y: indexInLevel * spacingY + level * 150 // Diagonal
      },
      style: {
        background: '#15243e',
        color: 'white',
        width: 175,
        height: 70,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      },
      sourcePosition: 'right',
      targetPosition: 'left',
      selectable: true
    };
  });
};

// Paso 3: Crear edges según relatedStatus
const createEdges = (allStatuses: TicketFlowGraph[]): Edge[] => {
  const edges: Edge[] = [];

  allStatuses.forEach((status) => {
    const sourceId = `${status.ticketStatusId}`;
    status.relatedStatus?.forEach((rel) => {
      edges.push({
        id: `${sourceId}-${rel.ticketStatusId}-${uuidv4()}`,
        source: sourceId,
        target: `${rel.ticketStatusId}`,
        type: 'smoothstep',
        style: {
          stroke: 'black',
          strokeWidth: 1.5
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: 'black'
        }
      });
    });
  });

  return edges;
};

// Hook principal
const useGetGanttDiagram = ({ id, setNodes, setEdges }: Props) => {
  const { processApi } = useApiContext();

  const getGanttDiagram = useQuery(
    [KEYS.client.ticketFlow.getGanttDiagram(), id],
    () => processApi.ProblemFlow.getGanttDiagram(id),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      onSuccess: ({ data }) => {
        const { initialStatus, allStatuses } = data?.data || {};
        const initialId = initialStatus.ticketStatusId;

        const levels = calculateLevels(initialId, allStatuses);
        const nodes = createNodes(allStatuses, levels, initialId);
        const edges = createEdges(allStatuses);

        setNodes(nodes);
        setEdges(edges);
      }
    }
  );

  return getGanttDiagram;
};

export default useGetGanttDiagram;
